// /inventory/products · product catalog management.
// Add / edit / retire SKUs. The Worker hard-deletes products with no ledger
// events and soft-deletes (active = 0) once history exists, so the ledger
// is never broken by a catalog change.
//
// Polished to the institutional design system:
//   - Sticky toolbar with shift filter + search + include-inactive toggle
//   - Dense grid table (inv-grid) with sortable columns
//   - displayName(p) strips redundant "{category} · " prefix
//   - ConfirmDialog replaces browser confirm() for destructive ops
//   - Toast for success/error feedback
//   - Modal now collects shift / variant / size / daily_target

import { useState, useEffect, useMemo } from 'react';
import * as api from '../lib/api.ts';
import { displayName } from '../lib/products.ts';
import { Modal } from '../components/Modal.jsx';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { Toast } from '../components/atoms.jsx';

const SHIFT_OPTIONS = [
  { key: 'all',   label: '全部' },
  { key: 'day',   label: '☀ 白班' },
  { key: 'night', label: '🌙 晚班' },
  { key: 'both',  label: '通用' }
];

export const InventoryProducts = () => {
  const [products, setProducts] = useState(null);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [shift, setShift] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'category', dir: 'asc' });
  const [toast, setToast] = useState(null);

  async function refresh() {
    try {
      setProducts(await api.listProducts({ includeInactive }));
      setErr('');
    } catch (e) { setErr(e?.message || '加载失败'); }
  }
  useEffect(() => { refresh(); /* eslint-disable-line */ }, [includeInactive]);

  async function confirmDelete() {
    if (!deleting) return;
    try {
      const r = await api.deleteProduct(deleting.id);
      await refresh();
      setToast({
        kind: 'success',
        msg: r.soft_deleted
          ? `「${deleting.name}」已停用 — 库存历史已保留`
          : `「${deleting.name}」已删除`
      });
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '删除失败' });
    } finally {
      setDeleting(null);
    }
  }

  async function doReactivate(p) {
    try {
      await api.updateProduct(p.id, { active: true });
      await refresh();
      setToast({ kind: 'success', msg: `「${p.name}」已恢复在售` });
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '操作失败' });
    }
  }

  function changeSort(key) {
    setSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));
  }

  const visibleRows = useMemo(() => {
    if (!products) return [];
    let rows = products;
    if (shift !== 'all') rows = rows.filter(p => p.shift === shift || p.shift === 'both');
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.variant || '').toLowerCase().includes(q) ||
        (p.size || '').toLowerCase().includes(q)
      );
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    const valOf = (p) => {
      switch (sort.key) {
        case 'name':     return p.name;
        case 'category': return p.category || '';
        case 'variant':  return p.variant || '';
        case 'size':     return p.size || '';
        case 'shift':    return p.shift;
        case 'active':   return p.active ? 0 : 1;     // active first
        default:         return p.category || '';
      }
    };
    return [...rows].sort((a, b) => {
      const av = valOf(a), bv = valOf(b);
      const primary = typeof av === 'string'
        ? av.localeCompare(bv, 'zh')
        : av - bv;
      if (primary !== 0) return primary * dir;
      return a.name.localeCompare(b.name, 'zh');
    });
  }, [products, shift, search, sort]);

  if (products === null && !err) return <div className="vava-card empty-state">加载中…</div>;

  const activeCount = (products ?? []).filter(p => p.active).length;
  const inactiveCount = (products ?? []).filter(p => !p.active).length;

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">管理产品</h1>
          <div className="vava-page-meta">
            {activeCount} 个在售{inactiveCount > 0 && ` · ${inactiveCount} 个已停用`}
          </div>
        </div>
        <div className="inv-page-actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + 添加产品
          </button>
        </div>
      </div>

      <div className="inv-toolbar">
        <div className="inv-segmented" role="tablist" aria-label="班次筛选">
          {SHIFT_OPTIONS.map(o => (
            <button key={o.key}
                    role="tab"
                    aria-selected={shift === o.key}
                    onClick={() => setShift(o.key)}>
              {o.label}
            </button>
          ))}
        </div>
        <div className="inv-search">
          <input type="text"
                 value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="搜 产品 / 品类 / 口味 / 规格…" />
        </div>
        <label className="inv-checkbox-label">
          <input type="checkbox" checked={includeInactive}
                 onChange={(e) => setIncludeInactive(e.target.checked)} />
          显示已停用
        </label>
        <span className="vava-hint" style={{ marginLeft: 8 }}>
          {visibleRows.length} / {(products ?? []).length}
        </span>
      </div>

      {err && <div className="att-error inv-error" role="alert">{err}</div>}

      {visibleRows.length === 0 ? (
        <div className="inv-grid-wrap">
          <div className="inv-empty">
            <div className="emoji">📦</div>
            <div className="title">
              {(products ?? []).length === 0
                ? '还没有产品'
                : `没有匹配「${search}」的产品`}
            </div>
            <div className="sub">
              {(products ?? []).length === 0
                ? '点「+ 添加产品」开始建立目录'
                : '调整筛选，或清空搜索框看全部'}
            </div>
          </div>
        </div>
      ) : (
        <div className="inv-grid-wrap">
          <table className="inv-grid">
            <thead>
              <tr>
                <SortHeader k="category" label="品类" sort={sort} onSort={changeSort} />
                <SortHeader k="name"     label="产品" sort={sort} onSort={changeSort} />
                <SortHeader k="variant"  label="口味" sort={sort} onSort={changeSort} />
                <SortHeader k="size"     label="规格" sort={sort} onSort={changeSort} />
                <SortHeader k="shift"    label="班次" sort={sort} onSort={changeSort} />
                <SortHeader k="active"   label="状态" sort={sort} onSort={changeSort} />
                <th className="num">操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(p => (
                <tr key={p.id} className={p.active ? '' : 'inv-row-inactive'}>
                  <td className="inv-name-sub">{p.category || '—'}</td>
                  <td className="inv-name">
                    {displayName(p)}
                  </td>
                  <td className="inv-name-sub">{p.variant || '—'}</td>
                  <td className="inv-name-sub">{p.size || '—'}</td>
                  <td className="inv-name-sub">
                    {p.shift === 'day' ? '白班' : p.shift === 'night' ? '晚班' : '通用'}
                  </td>
                  <td>
                    <span className={`status-badge ${p.active ? 'user-state-active' : 'user-state-deactivated'}`}>
                      {p.active ? '在售' : '已停用'}
                    </span>
                  </td>
                  <td className="inv-row-actions">
                    <button onClick={() => setEditing(p)}>编辑</button>
                    {p.active ? (
                      <button className="inv-wipe-btn"
                              onClick={() => setDeleting(p)}>停用</button>
                    ) : (
                      <button onClick={() => doReactivate(p)}>恢复</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="inv-footnote">
        产品名以「品类 · 口味 · 规格」自动组合；显示时已去重前缀。
        有库存记录的产品停用后历史保留（软删除），无记录的直接删除。
      </div>

      {creating && (
        <ProductModal onClose={() => setCreating(false)} onSaved={() => refresh()}
                      onToast={setToast} />
      )}
      {editing && (
        <ProductModal product={editing} onClose={() => setEditing(null)}
                      onSaved={() => refresh()} onToast={setToast} />
      )}
      {deleting && (
        <ConfirmDialog
          title="停用产品？"
          message={
            <>
              将「<strong>{deleting.name}</strong>」标记为已停用。
              库存记录会保留 — 之后可在「显示已停用」筛选下找回并恢复。
            </>
          }
          confirmLabel="停用"
          kind="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)} />
      )}
      {toast && <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
    </>
  );
};

// ============================================================================
// SortHeader (same as InventoryStats — extract to shared if used a 3rd time)
// ============================================================================

const SortHeader = ({ k, label, num, sort, onSort }) => {
  const active = sort.key === k;
  const arrow = !active ? '' : (sort.dir === 'asc' ? '↑' : '↓');
  return (
    <th className={`sortable ${num ? 'num' : ''}`} onClick={() => onSort(k)}>
      {label} {arrow && <span className="sort-ind">{arrow}</span>}
    </th>
  );
};

// ============================================================================
// ProductModal — create or edit. Now also collects shift / variant / size /
// daily_target so the schema's full shape is editable from the UI.
// ============================================================================

const ProductModal = ({ product, onClose, onSaved, onToast }) => {
  const isEdit = !!product;
  const [name, setName]         = useState(product?.name || '');
  const [category, setCategory] = useState(product?.category || '');
  const [variant, setVariant]   = useState(product?.variant || '');
  const [size, setSize]         = useState(product?.size || '');
  const [shift, setShift]       = useState(product?.shift || 'both');
  const [dailyTarget, setDailyTarget] = useState(
    product?.dailyTarget != null ? String(product.dailyTarget) : ''
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function save() {
    setErr('');
    if (!name.trim()) return setErr('产品名不能为空');
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        category: category.trim() || null,
        variant:  variant.trim()  || null,
        size:     size.trim()     || null,
        shift
      };
      if (dailyTarget !== '' && !isNaN(parseInt(dailyTarget, 10))) {
        payload.daily_target = parseInt(dailyTarget, 10);
      } else if (dailyTarget === '') {
        payload.daily_target = null;
      }
      if (isEdit) await api.updateProduct(product.id, payload);
      else        await api.createProduct(payload);
      await onSaved();
      onToast?.({ kind: 'success', msg: isEdit ? '已保存' : '已添加' });
      onClose();
    } catch (e) {
      setErr(e?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={isEdit ? `编辑产品 · ${product.name}` : '添加产品'}
      onClose={onClose}
      width={480}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>取消</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? '保存中…' : (isEdit ? '保存' : '添加')}
          </button>
        </>
      }>
      <div className="vava-field">
        <label className="vava-label">名称 *</label>
        <input className="vava-input" value={name}
               onChange={(e) => setName(e.target.value)}
               placeholder="例：经典6寸 · 草莓" disabled={saving} autoFocus />
        <div className="vava-hint">建议格式：「品类 · 口味 · 规格」</div>
      </div>
      <div className="vava-field-row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div className="vava-field" style={{ marginBottom: 0 }}>
          <label className="vava-label">品类</label>
          <input className="vava-input" value={category}
                 onChange={(e) => setCategory(e.target.value)}
                 placeholder="例：经典6寸" disabled={saving} />
        </div>
        <div className="vava-field" style={{ marginBottom: 0 }}>
          <label className="vava-label">口味</label>
          <input className="vava-input" value={variant}
                 onChange={(e) => setVariant(e.target.value)}
                 placeholder="例：草莓" disabled={saving} />
        </div>
      </div>
      <div className="vava-field-row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:14 }}>
        <div className="vava-field" style={{ marginBottom: 0 }}>
          <label className="vava-label">规格</label>
          <input className="vava-input" value={size}
                 onChange={(e) => setSize(e.target.value)}
                 placeholder='例：6"' disabled={saving} />
        </div>
        <div className="vava-field" style={{ marginBottom: 0 }}>
          <label className="vava-label">班次</label>
          <select className="vava-input" value={shift}
                  onChange={(e) => setShift(e.target.value)} disabled={saving}>
            <option value="both">通用</option>
            <option value="day">☀ 白班</option>
            <option value="night">🌙 晚班</option>
          </select>
        </div>
      </div>
      <div className="vava-field">
        <label className="vava-label">单 SKU 日产目标 (可选)</label>
        <input className="vava-input" type="number" min="0" step="1"
               value={dailyTarget}
               onChange={(e) => setDailyTarget(e.target.value)}
               placeholder="留空 = 不追踪此 SKU 的目标" disabled={saving} />
      </div>
      {err && <div className="att-error" role="alert">{err}</div>}
    </Modal>
  );
};
